import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import { History, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { type TaxonomyFieldTemplate } from '@/entities/taxonomy-field-template';

interface FieldActionMenuProps {
  template: TaxonomyFieldTemplate;
  /** Omitted for a viewer who cannot write; the menu keeps its read actions. */
  onEdit?: () => void;
  onHistory: () => void;
  /** Omitted for a viewer who cannot write. */
  onDelete?: () => void;
}

/**
 * Row actions for a reusable field definition.
 *
 * Every action only RAISES intent — the page owns the modals. That isn't
 * ceremony: a confirm dialog rendered in here would be unmounted by its own
 * success, because deleting the template drops the row, and a Radix dialog
 * that unmounts while open never restores `body { pointer-events }`. The whole
 * page silently stops accepting clicks. Keeping the dialogs above the rows
 * makes that unreachable.
 */
export function FieldActionMenu({ template, onEdit, onHistory, onDelete }: FieldActionMenuProps) {
  const { t } = useTranslation('knowledge');
  return (
    // Portals bubble events through the REACT tree, not the DOM tree, so
    // without this the menu's own clicks reach the row and open the editor.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <span className="contents" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="mini"
            aria-label={t('taxonomy.fieldActionMenu.actionsFor', { name: template.name })}
          >
            <MoreVertical className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {onEdit && (
            <DropdownMenuItem onSelect={onEdit}>
              <Pencil className="size-4" />
              {t('taxonomy.fieldActionMenu.edit')}
            </DropdownMenuItem>
          )}
          {/* The change log is a read, so it survives without write permission
              — the audit trail is the one thing a viewer still has use for. */}
          <DropdownMenuItem onSelect={onHistory}>
            <History className="size-4" />
            {t('taxonomy.fieldActionMenu.history')}
          </DropdownMenuItem>
          {onDelete && (
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onSelect={onDelete}
            >
              <Trash2 className="size-4" />
              {t('taxonomy.fieldActionMenu.removeFromLibrary')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}

FieldActionMenu.displayName = 'FieldActionMenu';
